import type { FC, ReactNode } from 'react'

import { useCallback, useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import { ArrowLeft, ArrowRight } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Reveal } from '@/components/generals/reveal'
import { Section } from '@/components/generals/section'
import { SectionHeading } from '@/components/generals/section-heading'
import { clientQuotes } from '@/constants/content'

import {
    TheoLogo,
    AmsterdamLogo,
    SavannahLogo,
    AuraLogo
} from '@/components/generals/logos'

const brandLogos = [TheoLogo, AmsterdamLogo, SavannahLogo, AuraLogo]

export const Clients: FC = (): ReactNode => {
    const trackRef = useRef<HTMLUListElement | null>(null)
    const [canScrollBack, setCanScrollBack] = useState(false)
    const [canScrollOn, setCanScrollOn] = useState(true)

    const syncArrows = useCallback((): void => {
        const track = trackRef.current

        if (!track) {
            return
        }

        const maxScroll = track.scrollWidth - track.clientWidth

        setCanScrollBack(track.scrollLeft > 8)
        setCanScrollOn(track.scrollLeft < maxScroll - 8)
    }, [])

    useEffect(() => {
        const track = trackRef.current

        if (!track) {
            return
        }

        syncArrows()
        track.addEventListener('scroll', syncArrows, { passive: true })
        window.addEventListener('resize', syncArrows)

        return () => {
            track.removeEventListener('scroll', syncArrows)
            window.removeEventListener('resize', syncArrows)
        }
    }, [syncArrows])

    const scrollBy = (direction: 1 | -1): void => {
        const track = trackRef.current

        if (!track) {
            return
        }

        const card = track.querySelector('li')
        const step = card ? card.getBoundingClientRect().width + 24 : track.clientWidth * 0.8

        track.scrollBy({ left: step * direction, behavior: 'smooth' })
    }

    return (
        <Section id='clients'>
            <SectionHeading
                eyebrow='Our Clients'
                segments={[
                    { text: 'Hear it directly from' },
                    { text: 'our', serif: true },
                    { text: 'clients.' }
                ]}
                subtitle='Hear what our clients have to say. Our testimonials reflect the satisfaction our clients have in our services.'
            />

            <Reveal
                y={28}
                blur={10}
                delay={0.1}
                className='relative mt-14 w-full lg:mt-20'
            >
                <ul
                    ref={trackRef}
                    className='no-scrollbar flex snap-x snap-mandatory items-stretch gap-6 overflow-x-auto overflow-y-hidden pb-1'
                >
                    {clientQuotes.map((client, index) => {
                        const BrandLogo = brandLogos[index]

                        return (
                            <li
                                key={client.name}
                                className='w-[84vw] max-w-[420px] shrink-0 snap-center sm:w-[380px]'
                            >
                                <article className='group flex h-full flex-col gap-5 rounded-xl bg-surface-1 p-3 ring-1 ring-white/8 transition-colors duration-500 ease-[cubic-bezier(0.44,0,0.56,1)] hover:ring-white/16'>
                                    <div className='relative aspect-4/3 w-full overflow-hidden rounded-lg bg-black'>
                                        <Image
                                            src={client.image}
                                            alt={client.alt}
                                            fill
                                            sizes='(max-width: 640px) 84vw, 380px'
                                            className='object-cover opacity-55 transition-transform duration-[1200ms] ease-[cubic-bezier(0.44,0,0.56,1)] group-hover:scale-105'
                                        />

                                        <div
                                            aria-hidden='true'
                                            className='absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/45'
                                        />

                                        <BrandLogo
                                            aria-hidden='true'
                                            className='absolute top-1/2 left-1/2 h-8 w-auto max-w-[62%] -translate-x-1/2 -translate-y-1/2 text-white'
                                        />

                                        <span className='sr-only'>
                                            {client.brand}
                                        </span>
                                    </div>

                                    <div className='flex flex-1 flex-col justify-between gap-6 px-2 pb-3'>
                                        <p className='text-[15px] leading-[1.45] tracking-[-0.02em] text-white/56'>
                                            {client.quote}
                                        </p>

                                        <div className='flex flex-col gap-0.5'>
                                            <p className='text-sm leading-[1.4] font-medium tracking-[-0.02em] text-white'>
                                                {client.name}
                                            </p>

                                            <p className='text-sm leading-[1.4] tracking-[-0.02em] text-white/40'>
                                                {client.role}
                                            </p>
                                        </div>
                                    </div>
                                </article>
                            </li>
                        )
                    })}
                </ul>

                <div className='mt-8 flex items-center justify-center gap-3'>
                    <button
                        type='button'
                        aria-label='Previous client'
                        disabled={!canScrollBack}
                        onClick={() => scrollBy(-1)}
                        className={cn(
                            'flex size-11 cursor-pointer items-center justify-center rounded-full ring-1 transition-all duration-300 ease-[cubic-bezier(0.44,0,0.56,1)]',
                            canScrollBack
                                ? 'bg-surface-5 text-white ring-white/12 hover:bg-[#8b5cf6] hover:ring-[#8b5cf6]'
                                : 'cursor-not-allowed bg-surface-1 text-white/25 ring-white/8'
                        )}
                    >
                        <ArrowLeft className='size-4' />
                    </button>

                    <button
                        type='button'
                        aria-label='Next client'
                        disabled={!canScrollOn}
                        onClick={() => scrollBy(1)}
                        className={cn(
                            'flex size-11 cursor-pointer items-center justify-center rounded-full ring-1 transition-all duration-300 ease-[cubic-bezier(0.44,0,0.56,1)]',
                            canScrollOn
                                ? 'bg-[#8b5cf6] text-white ring-[#8b5cf6] hover:bg-[#a276ff]'
                                : 'cursor-not-allowed bg-surface-1 text-white/25 ring-white/8'
                        )}
                    >
                        <ArrowRight className='size-4' />
                    </button>
                </div>
            </Reveal>
        </Section>
    )
}
